import type { Triggers, TriggerItem } from '@pelvia/shared/hooks/patient/usePainPatterns'

/**
 * Trigger/protector correlations: daily-log variables ranked by how strongly
 * they track with higher (red) or lower (green) pain. Renders nothing until
 * at least one correlation exists in either direction.
 */
export default function TriggersCard({ triggers }: { triggers?: Triggers }) {
  if (!triggers || (triggers.worse.length === 0 && triggers.better.length === 0)) {
    return null
  }

  return (
    <section className="card">
      <h3 className="pp-card-title">Triggers &amp; protectors</h3>
      <p className="pp-card-desc">
        Correlations between your daily logs and pain across {triggers.total_data_points}{' '}
        days. Correlation isn't causation — keep tracking to confirm.
      </p>
      {triggers.worse.length > 0 && (
        <div className="pp-trigger-group">
          <div className="pp-trigger-grouplabel">Linked to higher pain</div>
          {triggers.worse.slice(0, 5).map((t) => (
            <TriggerRow key={t.variable} item={t} />
          ))}
        </div>
      )}
      {triggers.better.length > 0 && (
        <div className="pp-trigger-group">
          <div className="pp-trigger-grouplabel">Linked to lower pain</div>
          {triggers.better.slice(0, 5).map((t) => (
            <TriggerRow key={t.variable} item={t} />
          ))}
        </div>
      )}
    </section>
  )
}

// A single trigger correlation row: label, strength bar (green=protective,
// red=aggravating), and the sample size.
function TriggerRow({ item }: { item: TriggerItem }) {
  const pct = Math.min(100, Math.abs(item.correlation) * 100)
  const protective = item.direction === 'better'
  const lag =
    item.lag === 'next_day' ? ' (next day)' : item.lag === 'two_days' ? ' (2 days)' : ''
  return (
    <div className="pp-trigger-row">
      <div className="pp-trigger-name">
        {item.label}
        {lag}
      </div>
      <div className="pp-trigger-bar-wrap">
        <div
          className="pp-trigger-bar"
          style={{ width: `${pct}%`, background: protective ? '#4a8c5c' : 'var(--danger)' }}
        />
      </div>
      <div className="pp-symptom-count">{item.n}d</div>
    </div>
  )
}
